import { SUPPORTED_MIME_TYPES } from '@/services/speech';

/**
 * 检测是否为 macOS
 */
export function isMacOS(): boolean {
  return navigator.userAgent.includes('Mac');
}

/**
 * 检测是否为 Continuity 远程设备（iPhone/iPad）
 *
 * Continuity 设备的 label 格式: "\"用户的 iPhone\"的麦克风"
 * 特征：以中英文引号开头
 */
function isContinuityDevice(label: string): boolean {
  return label.startsWith('"') || label.startsWith('"');
}

/**
 * 获取首选麦克风设备 ID
 *
 * 仅在 macOS 上且系统默认设备是 Continuity 远程设备时才干预，
 * 其他情况使用系统默认设备，尊重用户的系统设置。
 */
export async function getPreferredAudioDeviceId(): Promise<string | undefined> {
  // 只在 macOS 上启用检测，其他平台使用系统默认
  if (!isMacOS()) {
    return undefined;
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter(device => device.kind === 'audioinput');

    // 获取默认设备
    const defaultDevice = audioInputs.find(d => d.deviceId === 'default');

    // 检查默认设备是否是 Continuity 设备，如果不是则使用系统默认
    if (!defaultDevice || !isContinuityDevice(defaultDevice.label)) {
      return undefined;
    }

    // 默认设备是 Continuity，需要寻找替代设备
    const nonDefaultInputs = audioInputs.filter(d => d.deviceId !== 'default');

    // 优先使用 Built-in 设备
    const builtInDevice = nonDefaultInputs.find(d =>
      d.label.includes('Built-in') || d.label.includes('内置'),
    );

    if (builtInDevice) {
      return builtInDevice.deviceId;
    }

    // 其次使用其他非 Continuity 本地设备
    const localDevice = nonDefaultInputs.find(d => !isContinuityDevice(d.label));

    return localDevice?.deviceId;
  } catch {
    return undefined;
  }
}

/**
 * 获取浏览器支持的音频 MIME 类型
 */
export function getSupportedMimeType(): string {
  for (const type of SUPPORTED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return 'audio/webm';
}

/**
 * 检查浏览器是否支持录音
 */
export function isRecordingSupported(): boolean {
  return !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
}

/**
 * 格式化时长为 MM:SS 格式
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
